import React, { useEffect, useState } from "react";
import BounceLoader from "react-spinners/BounceLoader";

import routeBuilder from "../../../../../../../services/axios/routeBuilder";
import type { RouteBuilderProperty } from "../../../../../../../types/routeBuilder";
import { formatAddress } from "../../../../RoutePage/Routes/utils";
import { resolveRouteColorPalette } from "../../../../../../../components/Maps/ActiveRoutes/routeColor";
import { useRouteBuilder } from "../../../context/RouteBuilderContext";
import {
  propertyMatchesSearch,
  propertySearchRouteChips,
} from "../../../lib/helpers";

const SEARCH_DELAY_MS = 250;

export default function AddPropertyVisit({ routeId }: { routeId: string }) {
  const { session, addPropertyVisit } = useRouteBuilder();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<RouteBuilderProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const companyId = session?.companyId;
  const season = session?.inputs.season;

  useEffect(() => {
    if (!open || !companyId || !season) {
      setResults([]);
      setLoading(false);
      setFailed(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(
      async () => {
        setLoading(true);
        setFailed(false);
        try {
          const properties = await routeBuilder.loadBuilderProperties({
            companyId,
            season,
            search,
            take: 30,
            signal: controller.signal,
            includeAssignedRoutes: true,
          });
          if (!controller.signal.aborted) setResults(properties);
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error(error);
            setFailed(true);
          }
        } finally {
          setLoading(false);
        }
      },
      search.trim() ? SEARCH_DELAY_MS : 0,
    );

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [companyId, open, search, season]);

  if (!session) return null;
  const route = session.routes.find((candidate) => candidate.id === routeId);
  if (!route) return null;
  const visibleResults = results.filter((property) =>
    propertyMatchesSearch(property, search),
  );

  if (!open) {
    return (
      <div className="py-1 text-center">
        <button
          type="button"
          data-testid={`add-property-${routeId}`}
          className="text-xs font-medium text-slate-500 underline hover:text-snow-primary"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
        >
          + Add Property
        </button>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-sky-200 bg-white p-2 shadow-sm"
      data-testid={`add-property-search-${routeId}`}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          type="text"
          value={search}
          placeholder="Search properties…"
          aria-label="Search properties"
          autoComplete="off"
          data-testid={`add-property-search-input-${routeId}`}
          className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-xs outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-300"
          onChange={(event) => setSearch(event.target.value)}
        />
        <button
          type="button"
          aria-label="Close property search"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          onClick={() => {
            setOpen(false);
            setSearch("");
          }}
        >
          ×
        </button>
      </div>

      {loading && visibleResults.length === 0 ? (
        <div className="flex h-16 items-center justify-center">
          <BounceLoader
            color="#082D4A"
            size={20}
            aria-label="Loading matching properties"
            data-testid="property-search-loader"
          />
        </div>
      ) : failed && visibleResults.length === 0 ? (
        <p className="px-1 py-3 text-center text-[11px] text-rose-600">
          Could not load matching properties.
        </p>
      ) : visibleResults.length === 0 ? (
        <p className="px-1 py-3 text-center text-[11px] text-slate-500">
          {search.trim() ? "No matching properties" : "No properties available"}
        </p>
      ) : (
        <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto">
          {visibleResults.map((property) => {
            const existingCount = route.stops.filter(
              (stop) => stop.propertyId === property.jobId,
            ).length;
            const addressLine = formatAddress(property.address ?? {});
            const routeChips = propertySearchRouteChips(property, session);
            return (
              <li key={property.jobId}>
                <button
                  type="button"
                  data-testid={`add-property-result-${property.jobId}`}
                  className="w-full rounded-md px-2 py-1.5 text-left hover:bg-sky-50"
                  onPointerDown={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    addPropertyVisit(routeId, property);
                  }}
                >
                  <span className="block truncate text-xs font-semibold text-snow-primary">
                    {property.name}
                  </span>
                  {addressLine ? (
                    <span className="mt-0.5 block whitespace-normal text-[11px] leading-snug text-slate-500">
                      {addressLine}
                    </span>
                  ) : null}
                  {routeChips.length > 0 ? (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {routeChips.map((assigned) => {
                        const palette = resolveRouteColorPalette(
                          assigned.color,
                        );
                        return (
                          <span
                            key={assigned.id}
                            data-testid={`add-property-route-chip-${assigned.id}`}
                            className={`inline-block rounded-md px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ring-slate-200 ${palette.text} ${palette.bgLight}`}
                          >
                            {assigned.name}
                          </span>
                        );
                      })}
                    </span>
                  ) : null}
                  {existingCount > 0 ? (
                    <span className="mt-0.5 block text-[10px] font-medium text-violet-700">
                      Already on this route {existingCount} time
                      {existingCount === 1 ? "" : "s"}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
